import { useMemo } from 'react';
import { useAccountsApiCall } from '~/adapters/services/accounts/accountsApiCall';
import { ApiCall } from '~/adapters/services/shared/apiCall';
import { CreateAddressDto, PostalAddress, UpdateAddressDto } from './types';

export interface GetAddressesProps {
    accountId: number;
}

export interface CreateAddressesProps {
    accountId: number;
    addressDto: CreateAddressDto;
}

export interface UpdateAddressesProps {
    id: number;
    addressDto: UpdateAddressDto;
}

export interface DeleteAddressesProps {
    id: number;
}

class PostalAddressesRepository {
    constructor(private readonly accountsApiCall: ApiCall) {}

    public getPostalAddresses = async ({ accountId }: GetAddressesProps): Promise<Array<PostalAddress>> => {
        const params = { sort: '-isDefault,-updatedAt' };
        const response = await this.accountsApiCall<PostalAddress[]>(`/accounts/${accountId}/postal-addresses`, {
            params,
        });
        return response.data;
    };

    public createPostalAddress = async ({ accountId, addressDto }: CreateAddressesProps): Promise<PostalAddress> => {
        const response = await this.accountsApiCall<PostalAddress>(`/accounts/${accountId}/postal-addresses`, {
            method: 'POST',
            data: addressDto,
            headers: { 'Content-Type': 'application/json' },
        });
        return response.data;
    };

    public updatePostalAddress = async ({ id, addressDto }: UpdateAddressesProps): Promise<PostalAddress> => {
        const response = await this.accountsApiCall<PostalAddress>(`/postal-addresses/${id}`, {
            method: 'PUT',
            data: addressDto,
            headers: { 'Content-Type': 'application/json' },
        });
        return response.data;
    };

    public deletePostalAddress = async ({ id }: DeleteAddressesProps): Promise<void> => {
        await this.accountsApiCall<void>(`/postal-addresses/${id}`, { method: 'DELETE' });
    };
}

export const usePostalAddressesRepository = () => {
    const accountsApiCall = useAccountsApiCall();

    return useMemo(() => new PostalAddressesRepository(accountsApiCall), []);
};
